import { useContext, useEffect, useLayoutEffect, useMemo, useState } from "react";
import { FilterMatchMode } from "primereact/api";
import { DataTable } from "iuvity-ui-components/dist/datatable";
import { FormSkeleton } from "..";
import { TableHeader } from "../datatable/TableHeader";
import { CustomDialog, DialogProps } from "../dialog/CustomDialog";
import { Property } from "../../interfaces";
import { AppContext } from "../../context/index";
import { BoxFull, Button, Calendar } from "iuvity-ui-components";
import { Controller, useForm, useWatch } from "react-hook-form";
import { Column } from 'primereact/column'
import { AgentsFormSchema, TenantCreatorSchema } from "../../validations/schemas";
import { REVALIDATE_FORM_PROPS } from "../../constants";
import { MultiSelect } from 'primereact/multiselect'

interface AgentsInfoProps {
  title: string;
  data: any[];
  actionFn: Function;
  reloadFn: Function;
}

const filterDefaultValues = {
  name: '',
  version: '',
  url: '',
  modifiedat: ''
};

export const RemovedAgents: React.FC<AgentsInfoProps> = ({
  title,
  data,
  actionFn,
  reloadFn,
}) => {
  const {
    t,
    appState: { language, selectedLabel, pageFilter },
    setAppState
  } = useContext(AppContext);

  const form = useForm({
    mode: "onChange",
    delayError: 1200,
    reValidateMode: "onChange",
    defaultValues: pageFilter
      ? {
          name: pageFilter?.name || '',
          version: pageFilter?.version || '',
          url: pageFilter?.url || '',
          modifiedat: pageFilter?.modifiedat || '',
        }
      : filterDefaultValues,
  });
  
  const { control, setValue, getValues } = form;
  const nameValues = useWatch({
    control,
    name: "name",
  });

  const versionValues = useWatch({
    control,
    name: "version",
  });

  const urlValues = useWatch({
    control,
    name: "url",
  });

  const [modalProps, setModalProps] = useState<DialogProps>({
    visible: false,
  } as DialogProps);
  const [filteredData, setFilteredData] = useState<any | null>(null);

  const columns = useMemo(() => {
    if (language) {
      return [
        {
          field: "name",
          header: t("Agents.nameLabel"),
        },
        {
          field: "version",
          header: t("Agents.versionLabel"),
        },
        {
          field: "url",
          header: t("Agents.urlLabel"),
        },
        {
          field: "modifiedat",
          header: t("Agents.modifiedatLabel"),
        },
        {
          field: "actions",
          header: t("Agents.actionsLabel"),
        },
      ];
    } else {
      return [];
    }
  }, [t, language]);

  const [selectedColumns, setSelectedColumns] = useState(columns);
  const [filters, setFilters] = useState({
    global: { value: null, matchMode: FilterMatchMode.CONTAINS },
    name: { value: null, matchMode: FilterMatchMode.IN },
    version: { value: null, matchMode: FilterMatchMode.IN },
    url: { value: null, matchMode: FilterMatchMode.IN },
    modifiedat: { value: null, matchMode: FilterMatchMode.CONTAINS }
  });
  const [modifiedatValues, setModifiedatValues] = useState();

  const onEditAction = async (prop: Property) => {
    await actionFn("REACTIVE", [{...prop, status: "DRAFT"}]);
    reloadFn();
  };

  const handleModalProps = async (agent: any) => {
    setModalProps((prev) => ({
      ...prev,
      visible: true,
      title: t("Agents.deleteModalTitle"),
      content: `${t("Agents.reactiveModalContent1")} "${agent?.name}" ${t(
        "Agents.reactiveModalContent2"
      )}`,
      onConfirm: () => onEditAction(agent),
    }));
  };

  const onClearFilters = () => {
    setFilters({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS },
      name: { value: null, matchMode: FilterMatchMode.IN },
      version: { value: null, matchMode: FilterMatchMode.IN },
      url: { value: null, matchMode: FilterMatchMode.IN },
      modifiedat: { value: null, matchMode: FilterMatchMode.CONTAINS }
    })
    setAppState((prev) => ({
      ...prev,
      pageFilter: null,
    }));
    form.reset(filterDefaultValues);
  };

  useEffect(() => {
    if (language) {
      setSelectedColumns((prev) =>
        prev.map((col) => ({
          field: col.field,
          header: t(
            `Agents.${col.field === "type" ? "prop" : ""}${col.field}Label`
          ),
        }))
      );
    }
  }, [t, language, setSelectedColumns]);

  useEffect(() => {
    if (data && !pageFilter) {
      setFilteredData(data);
    } else if (!data) {
      setFilteredData(null);
    }
  }, [data, setFilteredData, setAppState, selectedLabel, pageFilter]);

  useLayoutEffect(() => {
    if (data && pageFilter) {
      setFilteredData(
        data.filter(
          (item: any) => {
            return ((form.getValues("name")?.length > 0
                ? item.name.toLowerCase().includes(form.getValues("name").toLowerCase())
                : true) &&
              (form.getValues("version")?.length > 0
                  ? item.shortname.toLowerCase().includes(form.getValues("version").toLowerCase())
                  : true) &&
              (form.getValues("url")?.length > 0
                  ? item.shortname.toLowerCase().includes(form.getValues("url").toLowerCase())
                  : true))
          }
        )
      );
    } else if (!data) {
      setFilteredData(data);
    }
  }, [data, setFilteredData, form, pageFilter]);

  const ItemsTemplate = (option: any) => {
    return (
      <div className="p-multiselect-representative-option">
        <span className="image-text">{option?.label}</span>
      </div>
    );
  };
  const filterTemplate = (options: any) => {
    const arrayOriginal = data
      ?.filter((agent: any) => agent.status === 'DELETED')
      ?.map((agent: any) => ({label: agent[`${options.field}`], value: agent[`${options.field}`]}))
      ?.sort((a: any, b: any) => a?.label?.localeCompare(b.label));
    
    const arrayWithoutDuplicates = arrayOriginal?.filter((object: any, currentIndex: any) => {
      return arrayOriginal.findIndex((duplicateObject: any) => 
        duplicateObject.label === object.label) === currentIndex;
    });

    return (
      <Controller
          name={options.field}
          control={control}
          rules={(AgentsFormSchema(t) as any)[options.field]}
          render={({ field }) => {
            field.onChange = (e) => {
              setValue(field.name, e.value, REVALIDATE_FORM_PROPS);
              options.filterCallback(e.value);
            };
            return (
              <>
                <label htmlFor={options.field} className="block lb mt-2 mb-1">
                  {t(`Agents.${options.field}Label`)}
                </label>
                <MultiSelect
                  display="chip"
                  options={arrayWithoutDuplicates}
                  optionLabel={'label'}
                  itemTemplate={ItemsTemplate}
                  placeholder={t(`Agents.${options.field}PlaceholderLabel`)} 
                  className="mb-2"
                  emptyFilterMessage={t("Agents.noAvailableMessage")}
                  filter
                  {...field}
                />
              </>
            );
          }}
      />
    )
  };

  const filterClearTemplate = (options: any) => {
    return (
      <Button
        label={t("Agents.clearFilterLabel")}
        variant="p-button-text"
        onClick={onClearFilters}
      />
    )
  };
  const filterApplyTemplate = (options: any) => {
    return (
      <Button
        label={t("Agents.applyFilterLabel")}
        className="p-button"
        disabled={
          !(
            nameValues.length > 0 ||
            versionValues.length > 0 ||
            urlValues.length > 0 ||
            modifiedatValues
          )
        }
        onClick={options.filterApplyCallback}
      />
    )
  };

  const formatDate = (value: any) => {
    return new Date(value).toLocaleDateString('en-US', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
  };
  const dateBodyTemplate = (rowData: any) => {
    return formatDate(rowData?.modifiedat);
  };
  const dateFilterTemplate = (options: any) => {
    setModifiedatValues(options.value);
    return (
        <Calendar
          label={t(`Agents.${options.field}Label`)}
          value={options.value}
          onChange={(e) => options.filterCallback(e.value, options.index)}
          dateFormat="mm/dd/yy"
          placeholder="mm/dd/yyyy"
          mask="99/99/9999"
          className="mt-2 mb-2"
        />
      )
  };

  return (
    <>
      <div className="py-0">
      <BoxFull className="dt-c mt-5 mb-4 mx-0">
      {data ? (
        <DataTable
        header={
          <TableHeader
            title={t("Agents.removedAgents")}
            columns={columns}
            selectedColumns={selectedColumns}
            toggleColumns={setSelectedColumns}
            filters={filters}
            setFilters={setFilters}
            reload={reloadFn}
            onClearFiltersColumn={onClearFilters}
          />
        }
        currentPageReportTemplate={t(
          "ConsoleTranslations.rowsPaginatorTemplateLabel"
        )}
        paginatorRight={t('ConsoleTranslations.itemsTextDatatableLabel')}
        emptyMessage={t("TableActions.emptyMessage")}
        rows={10}
        size="normal"
        filters={filters}
        sortField="id"
        dataKey="id"
        sortOrder={1}
        value={filteredData?.filter((agent: any) => agent?.status === 'DELETED').map((agent: any) => ({
          ...agent,
          status: t(`Agents.${agent.status.toLowerCase()}Label`),
          modifiedat: new Date(formatDate(agent.modifiedat)),
          actions: (
            <Button
              icon="iu"
              className="p-button-rounded p-button-success p-button-outlined act-btn"
              aria-label="Detail"
              tooltip={t("Agents.reactiveLabel")}
              tooltipOptions={{ position: "bottom" }}
              variant="p-button"
              onClick={() => handleModalProps(agent)}
              style={{
                color: "var(--successColor)",
              }}
            >
              <i className="iu iu-icon-check act-btn success" />
            </Button>
          ),
        }))}
        scrollable
        resizableColumns
        globalFilterFields={['name', 'version', 'url', 'modifiedat']}
        filterDisplay="menu"
        stateStorage="session"
        stateKey="filters-table"
      >
          {selectedColumns.map((col) => {
            return (
              <Column
                key={col.field}
                field={col.field}
                header={col.header}
                sortable={!(col.field === "actions")}
                align={col.field === "actions" ? "center" : "left"}
                headerStyle={col.field === "actions"
                ? { justifyContent: "center" }
                : undefined }
                style={{
                  boxShadow: (col.field === "actions")
                  ? "rgb(230 230 230 / 55%) -2px 0px, rgb(235 235 235 / 55%) -3px 0px, rgb(240 240 240 / 45%) -5px 0px"
                  : undefined,
                  maxWidth: (col.field === "actions") ? "132px" : undefined,
                  whiteSpace: "normal",
                  minWidth: (col.field === "actions") ? "8rem" : "10rem",
                }}
                frozen={col.field === "actions" ? true : false}
                alignFrozen={col.field === "actions" ? "right" : undefined}
                filterField={col.field}
                showFilterMatchModes={false}
                filterMenuStyle={{ width: '23rem'}}
                filter={col.field === "actions" ? false : true}
                filterElement={
                  ((col.field === "name" || col.field === "version" || col.field === "url")  && filterTemplate) ||
                  (col.field === "modifiedat" && dateFilterTemplate)
                }
                filterApply={filterApplyTemplate}
                filterClear={filterClearTemplate}
                dataType={col.field === "modifiedat" ? "date" : ""}
                body={col.field === "modifiedat" && dateBodyTemplate}
                
              />
            );
          })}
        </DataTable>
      ) : (
        <FormSkeleton length={8} />
      )}
      </BoxFull>
      </div>

      <CustomDialog
        title={modalProps.title}
        content={modalProps.content}
        visible={modalProps.visible}
        handleModalState={setModalProps}
        onConfirm={modalProps.onConfirm}
      />
    </>
  );
};
